import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from '@/contexts/OrganizationContext';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/fetchWithAuth";
import { UserProfile } from "@shared/types";
import { ROLES } from "@shared/constants";
import {
  getAssignableRolesForActor,
  getRoleLevel,
  getRoleLabel as getSharedRoleLabel,
  hasExecutiveLocationAccess,
  normalizeRole,
} from "@shared/role-access";
import { getInitials, getStatusColor } from "@/lib/utils";

interface TeamProps {
  user: UserProfile;
}

export default function Team({ user }: TeamProps) {
  // Get current organization from context with new isSwitching state
  const { currentOrganizationId, isSwitching } = useOrganization();
  
  // Debug organization changes
  useEffect(() => {
    console.log('🏢 Team page organization changed:', { currentOrganizationId });
  }, [currentOrganizationId]);
  
  // Use user's primary location ID or set it once locations load
  const [selectedLocation, setSelectedLocation] = useState<string>(user.locationId || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");
  
  // Debug activeTab changes
  useEffect(() => {
    console.log("ActiveTab changed to:", activeTab);
  }, [activeTab]);
  const [newLocation, setNewLocation] = useState({
    name: "",
    practiceName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    faxNumber: "",
    email: "",
    websiteUrl: "",
    hoursOfOperation: "",
    notes: "",
    status: "active",
  });
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  
  // New employee form states
  const [isAddTeamDialogOpen, setIsAddTeamDialogOpen] = useState(false);
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [newEmployeeData, setNewEmployeeData] = useState({
    // Work information
    fullName: "",
    email: "",
    role: ROLES.TEAM_MEMBER,
    isDoctor: false, // Doctor designation
    organizationId: currentOrganizationId || user.organizationId || "",
    locationId: "", // Primary location
    locationIds: [] as string[], // Multiple locations (up to 10)
    password: "",
    // Personal information
    personalEmail: "",
    personalPhone: "",
    homeAddress: "",
    birthDate: null as Date | null,
    maritalStatus: "",
    spouseName: "",
    numberOfChildren: 0,
    childrenNames: "",
    // Professional information
    licenseNumber: "", // Medical license number
    caqhNumber: "", // CAQH number
    npiNumber: "", // NPI number
    previousWorkExperience: "",
    professionalGoals: "",
    notes: "",
    emergencyContact: "",
    // Access controls
    canViewAllScorecards: false, // Permission to view all employee scorecards
    specificScorecardAccess: [] as string[], // IDs of specific employees who can view this person's scorecard
    // Headline access permissions
    headlineAccess: {
      executiveHeadlines: false,
      ceoHeadlines: false,
      virtualExecutiveHeadlines: false,
      teamLeadHeadlines: false,
      teamLeadGlobalHeadlines: false,
      teamMemberGlobalHeadlines: false,
      doctorsHeadlines: false,
      billingAndAuditingHeadlines: false,
      trainingHeadlines: false,
      trainerHeadlines: false,
      executiveTaskforceHeadlines: false
    }
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // For tracking selected locations in the multi-select
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Get locations accessible to the user
  const pageOrganizationId = currentOrganizationId || user.organizationId;
  const addTargetOrganizationId = user.isSuperAdmin
    ? (newEmployeeData.organizationId || pageOrganizationId || "")
    : (pageOrganizationId || "");

  const { data: locations } = useQuery({
    queryKey: [`/api/locations?organizationId=${pageOrganizationId}`],
    enabled: !!user && !!pageOrganizationId,
  });

  const { data: addDialogLocations } = useQuery<any[]>({
    queryKey: ['add-user-locations', addTargetOrganizationId],
    enabled: Boolean(addTargetOrganizationId) && (!user.isSuperAdmin || isAddTeamDialogOpen),
    queryFn: async () => {
      const response = await fetch(`/api/locations?organizationId=${encodeURIComponent(addTargetOrganizationId)}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to load locations (${response.status})`);
      }
      return response.json();
    },
  });
  
  // Set first location as default if none selected
  useEffect(() => {
    if ((!selectedLocation || selectedLocation === "") && 
        locations && 
        Array.isArray(locations) && 
        locations.length > 0) {
      setSelectedLocation(locations[0].id);
    }
  }, [locations, selectedLocation]);

  // Get team members with Agent B's enhanced organization switching
  const { data: teamMembers, isLoading, isError, error, refetch } = useQuery<any[]>({
    queryKey: ['users', currentOrganizationId], // Standardized query key format
    queryFn: async () => {
      const url = `/api/users?organizationId=${currentOrganizationId}`;
      console.log('🔍 Team query executing:', { url, currentOrganizationId });
      const result = await fetch(url, { credentials: 'include' });
      if (!result.ok) {
        throw new Error(`HTTP ${result.status}: ${result.statusText}`);
      }
      const data = await result.json();
      console.log('🔍 Team query result:', { url, count: data?.length || 0, data: data?.slice(0, 3) });
      return data;
    },
    enabled: !!currentOrganizationId && !isSwitching, // AGENT B FIX: Don't query during switching
    staleTime: 0, // Force fresh data for multi-organization
    refetchOnWindowFocus: false,
    refetchInterval: false,
    retry: 3,
  });
  
  // Log and debug data
  useEffect(() => {
    if (isError) {
      console.error("Error fetching team members:", error);
    }
    
    if (teamMembers) {
      console.log("Team members loaded:", teamMembers.length);
    }
  }, [isError, error, teamMembers]);
  
  const normalizedActorRole = normalizeRole(user.role) || user.role;
  const canManageUsers = user.isSuperAdmin === true;
  const assignableRoles = getAssignableRolesForActor(user.role, Boolean(user.isSuperAdmin));

  // Check if user is CEO, Executive, or super admin for location creation actions
  const isExecutiveOrHigher =
    Boolean(user.isSuperAdmin) ||
    normalizedActorRole === ROLES.CEO ||
    normalizedActorRole === ROLES.EXECUTIVE_TEAM;

  const { data: accessibleOrganizations = [] } = useQuery<any[]>({
    queryKey: ['organizations-user-access'],
    enabled: Boolean(user.isSuperAdmin),
    queryFn: async () => {
      const response = await fetch('/api/organizations/user-access', { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load organizations (${response.status})`);
      }
      return response.json();
    },
  });
  
  // Handle adding a new location
  const handleAddLocation = async () => {
    if (!newLocation.name.trim()) {
      toast({
        title: "Error",
        description: "Location name cannot be empty",
        variant: "destructive"
      });
      return;
    }
    
    setIsAddingLocation(true);
    
    try {
      const targetOrganizationId = currentOrganizationId || user.organizationId;
      if (!targetOrganizationId) {
        throw new Error("Organization is required to create a location");
      }

      // Send request to create new location
      await apiRequest('/api/locations', {
        method: 'POST',
        data: {
          ...newLocation,
          organizationId: targetOrganizationId,
        }
      });
      
      // Show success message
      toast({
        title: "Success",
        description: `Location "${newLocation.name}" has been added`
      });
      
      // Reset form and close dialog
      setNewLocation({
        name: "",
        practiceName: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        phoneNumber: "",
        faxNumber: "",
        email: "",
        websiteUrl: "",
        hoursOfOperation: "",
        notes: "",
        status: "active",
      });
      
      // Refresh locations data
      queryClient.invalidateQueries({ queryKey: [`/api/locations?organizationId=${targetOrganizationId}`] });
    } catch (error: any) {
      console.error("Error adding location:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to add location. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAddingLocation(false);
    }
  };
  
  // Handle location selection for multiple locations
  const handleLocationSelectionChange = (locationId: string) => {
    // Toggle the selected state for the location
    if (selectedLocationIds.includes(locationId)) {
      setSelectedLocationIds(selectedLocationIds.filter(id => id !== locationId));
    } else {
      // Only allow up to 10 locations
      if (selectedLocationIds.length < 10) {
        setSelectedLocationIds([...selectedLocationIds, locationId]);
      } else {
        toast({
          title: "Maximum Locations Reached",
          description: "An employee can be assigned to a maximum of 10 locations",
          variant: "destructive"
        });
      }
    }
  };

  // Update locationIds in employee data when selected locations change
  useEffect(() => {
    setNewEmployeeData((prev) => ({
      ...prev,
      locationIds: selectedLocationIds,
    }));
  }, [selectedLocationIds]);

  useEffect(() => {
    if (!user.isSuperAdmin) {
      setNewEmployeeData((prev) => ({
        ...prev,
        organizationId: currentOrganizationId || user.organizationId || prev.organizationId || "",
      }));
      return;
    }

    if (newEmployeeData.organizationId) return;
    const firstOrganizationId = accessibleOrganizations[0]?.id;
    if (!firstOrganizationId) return;

    setNewEmployeeData((prev) => ({
      ...prev,
      organizationId: firstOrganizationId,
    }));
  }, [
    user.isSuperAdmin,
    user.organizationId,
    currentOrganizationId,
    accessibleOrganizations,
    newEmployeeData.organizationId,
  ]);

  // Enforce doctor flag defaults for dedicated lead roles.
  useEffect(() => {
    if (newEmployeeData.role === ROLES.TEAM_LEAD_DOCTOR && !newEmployeeData.isDoctor) {
      setNewEmployeeData((prev) => ({ ...prev, isDoctor: true }));
      return;
    }
    if (newEmployeeData.role === ROLES.TEAM_LEAD_OPTICAL && newEmployeeData.isDoctor) {
      setNewEmployeeData((prev) => ({ ...prev, isDoctor: false }));
    }
  }, [newEmployeeData.role, newEmployeeData.isDoctor]);

  // Handle adding a new employee
  const handleAddEmployee = async () => {
    if (!newEmployeeData.fullName.trim() || !newEmployeeData.email.trim()) {
      toast({
        title: "Error",
        description: "Name and email are required fields",
        variant: "destructive"
      });
      return;
    }

    if (!newEmployeeData.password.trim()) {
      toast({
        title: "Error",
        description: "Temporary password is required",
        variant: "destructive"
      });
      return;
    }

    if (newEmployeeData.password.length < 10) {
      toast({
        title: "Error",
        description: "Temporary password must be at least 10 characters",
        variant: "destructive"
      });
      return;
    }

    if (newEmployeeData.password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Password confirmation does not match",
        variant: "destructive"
      });
      return;
    }
    
    // Validate doctor credential requirements
    if (newEmployeeData.isDoctor) {
      if (!newEmployeeData.licenseNumber || !newEmployeeData.licenseNumber.trim()) {
        toast({
          title: "Error", 
          description: "License number is required for doctors",
          variant: "destructive"
        });
        return;
      }
      
      if (!newEmployeeData.caqhNumber || !newEmployeeData.caqhNumber.trim()) {
        toast({
          title: "Error", 
          description: "CAQH number is required for doctors",
          variant: "destructive"
        });
        return;
      }
      
      if (!newEmployeeData.npiNumber || !newEmployeeData.npiNumber.trim()) {
        toast({
          title: "Error", 
          description: "NPI number is required for doctors",
          variant: "destructive"
        });
        return;
      }
    }
    
    if (!newEmployeeData.locationId) {
      toast({
        title: "Error",
        description: "Please select a primary location for this employee",
        variant: "destructive"
      });
      return;
    }
    
    setIsAddingEmployee(true);
    
    try {
      const targetOrganizationId = user.isSuperAdmin
        ? (newEmployeeData.organizationId || currentOrganizationId || user.organizationId || "")
        : (currentOrganizationId || user.organizationId || "");

      if (!targetOrganizationId) {
        throw new Error("Organization is required");
      }

      // Send request to create new employee
      const response = await apiRequest<any>("/api/users", {
        method: 'POST',
        data: {
          ...newEmployeeData,
          organizationId: targetOrganizationId,
          avatarUrl: null // Required by the schema
        }
      });
      
      // Show success message
      toast({
        title: "Success",
        description: `Employee "${newEmployeeData.fullName}" has been added`
      });

      if (response?.emailVerificationRequired) {
        toast({
          title: "Verification Required",
          description: `A verification code was sent via ${response?.verificationDelivery || 'email'}.`,
        });
      }
      
      // Reset form and selected locations
      setNewEmployeeData({
        // Work information
        fullName: "",
        email: "",
        role: ROLES.TEAM_MEMBER,
        isDoctor: false,
        organizationId: user.isSuperAdmin
          ? (accessibleOrganizations[0]?.id || "")
          : (currentOrganizationId || user.organizationId || ""),
        locationId: "", // Primary location
        locationIds: [] as string[], // Multiple locations (up to 10)
        password: "",
        // Personal information
        personalEmail: "",
        personalPhone: "",
        homeAddress: "",
        birthDate: null as Date | null,
        maritalStatus: "",
        spouseName: "",
        numberOfChildren: 0,
        childrenNames: "",
        // Professional information
        licenseNumber: "",
        caqhNumber: "",
        npiNumber: "",
        previousWorkExperience: "",
        professionalGoals: "",
        notes: "",
        emergencyContact: "",
        // Access controls
        canViewAllScorecards: false,
        specificScorecardAccess: [] as string[], // Reset specific employee scorecard access
        headlineAccess: {
          executiveHeadlines: false,
          ceoHeadlines: false,
          virtualExecutiveHeadlines: false,
          teamLeadHeadlines: false,
          teamLeadGlobalHeadlines: false,
          teamMemberGlobalHeadlines: false,
          doctorsHeadlines: false,
          billingAndAuditingHeadlines: false,
          trainingHeadlines: false,
          trainerHeadlines: false,
          executiveTaskforceHeadlines: false
        }
      });
      setConfirmPassword("");
      setSelectedLocationIds([]);
      setIsAddTeamDialogOpen(false);
      
      // Directly add the new employee to the displayMembers and force re-render
      const { password: _password, ...employeeFields } = newEmployeeData;
      const employeeData = {
        ...employeeFields,
        id: `new-${Date.now()}`, // Create a temporary ID for immediate display
        organizationId: targetOrganizationId,
        score: 85, // Default score for new employees
        status: 'green', // Default status
        location: displayLocations.find((loc: {id: string, name: string}) => 
          loc.id === newEmployeeData.locationId
        )?.name || 'Unknown'
      };
      
      
      // Invalidate the correct users query for this organization so UI refetches
      queryClient.invalidateQueries({ queryKey: ['users', currentOrganizationId] });
      
      // Optimistically append to currently displayed list for instant feedback
      queryClient.setQueryData<any[]>(['users', currentOrganizationId], (prev = []) => {
        return [...prev, employeeData];
      });
      
      // Ensure a server refetch runs immediately
      refetch();
      
    } catch (error: any) {
      console.error("Error adding employee:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add employee. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAddingEmployee(false);
    }
  };

  // Filter team members based on search, active tab, and selected location
  const filterTeamMembers = (members: any[], locationOverride: string = '') => {
    if (!Array.isArray(members)) {
      console.error("filterTeamMembers received non-array:", members);
      return [];
    }
    
    return members.filter(member => {
      // Skip invalid members
      if (!member || typeof member !== 'object') {
        console.warn("Invalid team member object in filter:", member);
        return false;
      }
      
      // Filter by search text
      const fullName = member.fullName || '';
      const role = member.role || '';
      const roleLabel = getRoleLabel(role) || '';
      
      const matchesSearch = searchQuery === "" || 
        fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        roleLabel.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by active role tab
      const normalizedRole = normalizeRole(role) || role;
      const matchesTab = activeTab === "all" || normalizedRole === activeTab;
      
      // Debug activeTab value
      if (!matchesTab && fullName === teamMembers?.[0]?.fullName) {
        console.debug("Current activeTab:", activeTab, "Member role:", role);
      }
      
      // Filter by selected location if one is selected
      let matchesLocation = true;
      
      // CEOs and executives should see all team members regardless of location
      const userRole = user?.role || '';
      const isExecutiveLevel = hasExecutiveLocationAccess(userRole, Boolean(user.isSuperAdmin));
      
      if (isExecutiveLevel) {
        matchesLocation = true;
      }
      // Override: If we're debugging with location override, skip location filtering entirely
      else if (locationOverride === '') {
        matchesLocation = true;
      }
      // Otherwise check if there's a selected location to filter by
      else if (selectedLocation && selectedLocation !== "") {
        // Reset matchesLocation to false by default, will set to true if match found
        matchesLocation = false;
        
        // Check primary location
        if (member.locationId === selectedLocation) {
          matchesLocation = true;
        } 
        // Check location string match
        else if (member.location && typeof member.location === 'string') {
          // Get the location name from id
          const locationObj = displayLocations.find(loc => loc.id === selectedLocation);
          if (locationObj && member.location.includes(locationObj.name)) {
            matchesLocation = true;
          }
        }
        // Or check in multi-location assignments
        else if (member.locationIds && Array.isArray(member.locationIds)) {
          matchesLocation = member.locationIds.includes(selectedLocation);
        }
        
        // Show all newly created team members (recent ones should be visible)
        if (member.createdAt) {
          const memberCreated = new Date(member.createdAt);
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          if (memberCreated > fiveMinutesAgo) {
            matchesLocation = true;
          }
        }
      }
      
      // Debug logging
      if (!matchesSearch || !matchesTab || !matchesLocation) {
        console.debug(`Member ${fullName} filtered out:`, 
          { matchesSearch, matchesTab, matchesLocation, role, locationOverride, selectedLocation });
      }
      
      return matchesSearch && matchesTab && matchesLocation;
    });
  };

  // Generate role label from enum value
  const getRoleLabel = (role: string) => {
    const normalizedLabel = getSharedRoleLabel(role);
    if (normalizedLabel) return normalizedLabel;

    const roleMap: Record<string, string> = {
      [ROLES.CEO]: 'CEO',
      [ROLES.EXECUTIVE_TEAM]: 'Executive',
      [ROLES.NEXT_GEN_EXECUTIVE_TEAM]: 'NextGen Executive',
      [ROLES.TRAINING_LEVEL]: 'Training Level',
      [ROLES.TEAM_LEAD]: 'Team Lead',
      [ROLES.TEAM_MEMBER]: 'Team Member',
      [ROLES.VIRTUAL_EXECUTIVE]: 'Virtual Executive',
      [ROLES.EXECUTIVE_VIRTUAL_TEAM]: 'Executive Virtual Team',
      [ROLES.VIRTUAL_TEAM_MEMBER]: 'Virtual Team Member',
      [ROLES.BILLING_AUDITING]: 'Billing-Auditing',
      [ROLES.BOLT_TASKFORCE]: 'Bolt Eye TaskForce',
      [ROLES.TRAINER]: 'Trainer'
    };
    
    return roleMap[role] || role;
  };

  // Mock data for development - Real team members provided by the user
  const mockTeamMembers = [
    {
      id: '1',
      fullName: 'Dr. Bolt',
      email: 'jwoolfson@bolteye.com',
      role: ROLES.CEO,
      score: 98,
      status: 'green',
      location: 'BOLT TaskForce'
    },
    {
      id: '2',
      fullName: 'Dr. Susan Reimbold',
      email: 'sreimbold@bolteye.com',
      role: ROLES.CEO,
      score: 96,
      status: 'green',
      location: 'REG'
    },
    {
      id: '3',
      fullName: 'Barbara Williams',
      email: 'bwilliams@bolteye.com',
      role: ROLES.EXECUTIVE_TEAM,
      score: 95,
      status: 'green',
      location: 'REG'
    },
    {
      id: '4',
      fullName: 'Dr. Sarah Arnette',
      email: 'sarnette@bolteye.com',
      role: ROLES.NEXT_GEN_EXECUTIVE_TEAM,
      score: 94,
      status: 'green',
      location: 'JCITY'
    },
    {
      id: '5',
      fullName: 'Morgan Lauderdale',
      email: 'mlauderdale@bolteye.com',
      role: ROLES.TEAM_LEAD,
      score: 93,
      status: 'green',
      location: 'LVL'
    },
    {
      id: '6',
      fullName: 'Tuany',
      email: 'tuany@bolteye.com',
      role: ROLES.VIRTUAL_EXECUTIVE,
      score: 92,
      status: 'green',
      location: 'Virtual'
    },
    {
      id: '7',
      fullName: 'Thalita',
      email: 'thalita@bolteye.com',
      role: ROLES.VIRTUAL_EXECUTIVE,
      score: 91,
      status: 'green',
      location: 'Virtual'
    },
    {
      id: '8',
      fullName: 'Jose',
      email: 'jose@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 90,
      status: 'green',
      location: 'BOLT TaskForce'
    },
    {
      id: '9',
      fullName: 'Dr. Lopp',
      email: 'dlopp@bolteye.com',
      role: ROLES.TEAM_LEAD,
      score: 89,
      status: 'green',
      location: 'LVL'
    },
    {
      id: '10',
      fullName: 'Jennifer Haily',
      email: 'jhaily@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 88,
      status: 'green',
      location: 'RVD'
    },
    {
      id: '11',
      fullName: 'Dr. Sarah Williford',
      email: 'swilliford@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 87,
      status: 'green',
      location: 'DEC'
    },
    {
      id: '12',
      fullName: 'Debra',
      email: 'debra@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 86,
      status: 'green',
      location: 'DEC'
    },
    {
      id: '13',
      fullName: 'Dr. Katylin',
      email: 'katylin@bolteye.com',
      role: ROLES.TEAM_LEAD,
      score: 85,
      status: 'green',
      location: 'ECOBB'
    },
    {
      id: '14',
      fullName: 'Stephanie',
      email: 'stephanie@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 84,
      status: 'green',
      location: 'ECOBB'
    },
    {
      id: '15',
      fullName: 'Dr. Katylin',
      email: 'katylin@bolteye.com',
      role: ROLES.TEAM_LEAD,
      score: 83,
      status: 'green',
      location: 'CTN'
    },
    {
      id: '16',
      fullName: 'Stephanie',
      email: 'stephanie@bolteye.com',
      role: ROLES.TEAM_MEMBER,
      score: 82,
      status: 'green',
      location: 'CTN'
    }
  ];

  // Mock location data for development
  const mockLocations = [
    { id: '1', name: 'LVL' }, // New locations as per user request
    { id: '2', name: 'RVD' },
    { id: '3', name: 'ECOBB' },
    { id: '4', name: 'JCITY' },
    { id: '5', name: 'REG' },
    { id: '6', name: 'DEC' },
    { id: '7', name: 'CTN' },
    { id: '8', name: 'BOLT TaskForce' },
    { id: '9', name: 'All Locations' }
  ];

  // Safely handle location data
  const displayLocations = locations && Array.isArray(locations) ? locations : mockLocations;
  const availableAddLocations = addDialogLocations && Array.isArray(addDialogLocations) && addDialogLocations.length > 0
    ? addDialogLocations
    : displayLocations;
  
  // Use real team members data from API
  const displayMembers = teamMembers || [];
  
  // Log members for debugging
  console.log("Display members count:", displayMembers.length);
  console.log("Actual server members:", teamMembers?.length || 0);
  
  // Log all team member details for debugging
  if (teamMembers) {
    teamMembers.forEach((member, idx) => {
      console.log(`Team member ${idx + 1}:`, {
        id: member.id,
        name: member.fullName,
        location: member.locationId,
        locationIds: member.locationIds,
        role: member.role
      });
    })
  }
  
  // Use the actual selected location for filtering
  // Filter members based on search, tab, and location selection
  const filteredMembers = filterTeamMembers(displayMembers, selectedLocation);
  console.log("Filtered members count:", filteredMembers.length);
  
  // Get unique roles for tabs, but only from valid members
  const uniqueRoles = Array.from(
    new Set(
      displayMembers
        .filter(member => member && member.role)
        .map(member => normalizeRole(member.role) || member.role),
    ),
  );
  uniqueRoles.sort((a, b) => getRoleLevel(b) - getRoleLevel(a));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Users and Groups</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="Team management and performance tracking"
          ></span>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Select
              value={selectedLocation}
              onValueChange={setSelectedLocation}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {displayLocations.map((location: {id: string, name: string}) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {isExecutiveOrHigher && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <span className="mdi mdi-map-marker-plus mr-2"></span>
                  Add Location
                </Button>
              </DialogTrigger>
              <DialogContent className="flex max-h-[90dvh] flex-col overflow-hidden">
                <DialogHeader className="shrink-0">
                  <DialogTitle>Add New Location</DialogTitle>
                  <DialogDescription>
                    Enter a location code and any available contact details. Fields marked optional may be left blank. Scroll to see all fields.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid min-h-0 gap-4 overflow-y-auto py-4 pr-2">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Location Code/Name *</Label>
                    <Input 
                      id="name"
                      value={newLocation.name}
                      onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                      placeholder="Enter location code (e.g. NYC1, BOS2)"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="practiceName">Practice Name (optional)</Label>
                    <Input 
                      id="practiceName"
                      value={newLocation.practiceName}
                      onChange={(e) => setNewLocation({...newLocation, practiceName: e.target.value})}
                      placeholder="Enter full practice name"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="address">Street Address (optional)</Label>
                    <Input 
                      id="address"
                      value={newLocation.address}
                      onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                      placeholder="Enter street address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div className="grid gap-2">
                      <Label htmlFor="city">City (optional)</Label>
                      <Input 
                        id="city"
                        value={newLocation.city}
                        onChange={(e) => setNewLocation({...newLocation, city: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="state">State (optional)</Label>
                      <Input 
                        id="state"
                        value={newLocation.state}
                        onChange={(e) => setNewLocation({...newLocation, state: e.target.value})}
                        placeholder="State"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="zipCode">Zip Code (optional)</Label>
                    <Input 
                      id="zipCode"
                      value={newLocation.zipCode}
                      onChange={(e) => setNewLocation({...newLocation, zipCode: e.target.value})}
                      placeholder="Enter zip code"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="phoneNumber">Phone Number (optional)</Label>
                    <Input 
                      id="phoneNumber"
                      value={newLocation.phoneNumber}
                      onChange={(e) => setNewLocation({...newLocation, phoneNumber: e.target.value})}
                      placeholder="Enter phone number"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="faxNumber">Fax Number (optional)</Label>
                    <Input 
                      id="faxNumber"
                      value={newLocation.faxNumber}
                      onChange={(e) => setNewLocation({...newLocation, faxNumber: e.target.value})}
                      placeholder="Enter fax number"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="email">Location Email (optional)</Label>
                    <Input 
                      id="email"
                      value={newLocation.email}
                      onChange={(e) => setNewLocation({...newLocation, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="websiteUrl">Website URL (optional)</Label>
                    <Input 
                      id="websiteUrl"
                      value={newLocation.websiteUrl}
                      onChange={(e) => setNewLocation({...newLocation, websiteUrl: e.target.value})}
                      placeholder="Enter website URL"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="hoursOfOperation">Hours of Operation (optional)</Label>
                    <Input 
                      id="hoursOfOperation"
                      value={newLocation.hoursOfOperation}
                      onChange={(e) => setNewLocation({...newLocation, hoursOfOperation: e.target.value})}
                      placeholder="e.g. Mon-Fri: 9am-5pm, Sat: 10am-2pm"
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={newLocation.status}
                      onValueChange={(value) => setNewLocation({...newLocation, status: value})}
                    >
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="under_construction">Under Construction</SelectItem>
                        <SelectItem value="planned">Planned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea 
                      id="notes"
                      value={newLocation.notes}
                      onChange={(e) => setNewLocation({...newLocation, notes: e.target.value})}
                      placeholder="Additional notes about this location"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter className="shrink-0 border-t pt-4">
                  <Button 
                    onClick={handleAddLocation} 
                    disabled={isAddingLocation || !newLocation.name.trim()}>
                    {isAddingLocation ? 'Adding...' : 'Add Location'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          {canManageUsers && (
          <Dialog open={isAddTeamDialogOpen} onOpenChange={setIsAddTeamDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <span className="mdi mdi-plus mr-2"></span>
                Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle>Add New Team Member</DialogTitle>
                <DialogDescription>
                  Enter the details of the new team member to add to the organization.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                <Tabs defaultValue="work">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="work">Work Info</TabsTrigger>
                    <TabsTrigger value="personal">Personal Info</TabsTrigger>
                    <TabsTrigger value="professional">Professional</TabsTrigger>
                    <TabsTrigger value="access">Access Controls</TabsTrigger>
                  </TabsList>
                  
                  {/* Work Information Tab */}
                  <TabsContent value="work" className="space-y-4 mt-4">
                    <div className="grid gap-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input 
                        id="fullName"
                        value={newEmployeeData.fullName}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, fullName: e.target.value})}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="email">Work Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={newEmployeeData.email}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, email: e.target.value})}
                        placeholder="Enter work email address"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password">Temporary Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newEmployeeData.password}
                        onChange={(e) => setNewEmployeeData({ ...newEmployeeData, password: e.target.value })}
                        placeholder="Set temporary password"
                      />
                      <p className="text-xs text-muted-foreground">
                        Team member will use this to sign in, then can change it in Settings.
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter temporary password"
                      />
                    </div>
                    <div className="grid gap-2">
                      {user.isSuperAdmin && (
                        <div className="grid gap-2">
                          <Label htmlFor="organizationId">Organization *</Label>
                          <Select
                            value={newEmployeeData.organizationId}
                            onValueChange={(value) => {
                              setSelectedLocationIds([]);
                              setNewEmployeeData({
                                ...newEmployeeData,
                                organizationId: value,
                                locationId: "",
                                locationIds: [],
                              });
                            }}
                          >
                            <SelectTrigger id="organizationId">
                              <SelectValue placeholder="Select organization" />
                            </SelectTrigger>
                            <SelectContent>
                              {accessibleOrganizations.map((organization: { id: string; name: string }) => (
                                <SelectItem key={organization.id} value={organization.id}>
                                  {organization.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center space-x-2 mb-2">
                        <Checkbox 
                          id="isDoctor" 
                          checked={newEmployeeData.isDoctor}
                          onCheckedChange={(checked) => 
                            setNewEmployeeData({
                              ...newEmployeeData, 
                              isDoctor: !!checked
                            })
                          }
                        />
                        <Label 
                          htmlFor="isDoctor" 
                          className="text-sm font-medium cursor-pointer"
                        >
                          Medical Doctor
                        </Label>
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                        Team Lead Doctor auto-enables medical flag. Team Lead Optical always keeps medical flag off.
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="role">Role *</Label>
                      <Select
                        value={newEmployeeData.role}
                        onValueChange={(value) => {
                          const normalizedRole = normalizeRole(value) || value;
                          setNewEmployeeData((prev) => ({
                            ...prev,
                            role: normalizedRole,
                            isDoctor:
                              normalizedRole === ROLES.TEAM_LEAD_DOCTOR
                                ? true
                                : normalizedRole === ROLES.TEAM_LEAD_OPTICAL
                                  ? false
                                  : prev.isDoctor,
                          }));
                        }}
                      >
                        <SelectTrigger id="role">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableRoles.map((roleValue) => (
                            <SelectItem key={roleValue} value={roleValue}>
                              {getRoleLabel(roleValue)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Primary Location */}
                    <div className="grid gap-2">
                      <Label htmlFor="locationId">Primary Location *</Label>
                      <Select
                        value={newEmployeeData.locationId}
                        onValueChange={(value) => setNewEmployeeData({...newEmployeeData, locationId: value})}
                      >
                        <SelectTrigger id="locationId">
                          <SelectValue placeholder="Select primary location" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableAddLocations.map((location: {id: string, name: string}) => (
                            location.id !== '9' && ( // Skip "All Locations" option
                              <SelectItem key={location.id} value={location.id}>
                                {location.name}
                              </SelectItem>
                            )
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Additional Locations (up to 10 total) */}
                    <div className="grid gap-2">
                      <div className="flex justify-between items-center">
                        <Label>Additional Locations ({selectedLocationIds.length}/10)</Label>
                        <Badge variant="outline">{selectedLocationIds.length} selected</Badge>
                      </div>
                      <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
                        {availableAddLocations.map((location: {id: string, name: string}) => (
                          location.id !== '9' && location.id !== newEmployeeData.locationId && (
                            <div key={location.id} className="flex items-center space-x-2 py-1">
                              <Checkbox 
                                id={`loc-${location.id}`}
                                checked={selectedLocationIds.includes(location.id)}
                                onCheckedChange={() => handleLocationSelectionChange(location.id)}
                                disabled={selectedLocationIds.length >= 10 && !selectedLocationIds.includes(location.id)}
                              />
                              <Label 
                                htmlFor={`loc-${location.id}`}
                                className="text-sm cursor-pointer"
                              >
                                {location.name}
                              </Label>
                            </div>
                          )
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">An employee can be assigned to up to 10 locations total.</p>
                    </div>
                  </TabsContent>
                  
                  {/* Personal Information Tab */}
                  <TabsContent value="personal" className="space-y-4 mt-4">
                    <div className="grid gap-2">
                      <Label htmlFor="birthDate">Date of Birth</Label>
                      <input 
                        id="birthDate"
                        type="date"
                        value={newEmployeeData.birthDate ? new Date(newEmployeeData.birthDate).toISOString().substring(0,10) : ""}
                        onChange={(e) => setNewEmployeeData({
                          ...newEmployeeData, 
                          birthDate: e.target.value ? new Date(e.target.value) : null
                        })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="personalEmail">Personal Email</Label>
                      <Input 
                        id="personalEmail"
                        type="email"
                        value={newEmployeeData.personalEmail}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, personalEmail: e.target.value})}
                        placeholder="Enter personal email address"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="personalPhone">Cell Phone</Label>
                      <Input 
                        id="personalPhone"
                        value={newEmployeeData.personalPhone}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, personalPhone: e.target.value})}
                        placeholder="Enter cell phone number"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="homeAddress">Home Address</Label>
                      <Textarea 
                        id="homeAddress"
                        value={newEmployeeData.homeAddress}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, homeAddress: e.target.value})}
                        placeholder="Enter home address"
                        rows={2}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="emergencyContact">Emergency Contact</Label>
                      <Textarea 
                        id="emergencyContact"
                        value={newEmployeeData.emergencyContact}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, emergencyContact: e.target.value})}
                        placeholder="Name and phone number"
                        rows={2}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="maritalStatus">Marital Status</Label>
                      <Select
                        value={newEmployeeData.maritalStatus}
                        onValueChange={(value) => setNewEmployeeData({...newEmployeeData, maritalStatus: value})}
                      >
                        <SelectTrigger id="maritalStatus">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Single">Single</SelectItem>
                          <SelectItem value="Married">Married</SelectItem>
                          <SelectItem value="Divorced">Divorced</SelectItem>
                          <SelectItem value="Widowed">Widowed</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="spouseName">Spouse Name</Label>
                      <Input 
                        id="spouseName"
                        value={newEmployeeData.spouseName}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, spouseName: e.target.value})}
                        placeholder="If applicable"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="numberOfChildren">Number of Children</Label>
                      <Input 
                        id="numberOfChildren"
                        type="number"
                        min="0"
                        value={newEmployeeData.numberOfChildren}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, numberOfChildren: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="childrenNames">Children's Names</Label>
                      <Textarea 
                        id="childrenNames"
                        value={newEmployeeData.childrenNames}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, childrenNames: e.target.value})}
                        placeholder="Names and ages of children"
                        rows={2}
                      />
                    </div>
                  </TabsContent>
                  
                  {/* Professional Information Tab */}
                  <TabsContent value="professional" className="space-y-4 mt-4">
                    {/* Medical credentials section */}
                    <div className="mb-4 pb-4 border-b dark:border-gray-700">
                      <h3 className="text-sm font-medium mb-3">Medical Credentials</h3>
                      
                      <div className="grid gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="licenseNumber">License Number {newEmployeeData.isDoctor && <span className="text-red-500">*</span>}</Label>
                          <Input 
                            id="licenseNumber"
                            value={newEmployeeData.licenseNumber}
                            onChange={(e) => setNewEmployeeData({...newEmployeeData, licenseNumber: e.target.value})}
                            placeholder="Enter medical license number"
                            required={newEmployeeData.isDoctor}
                          />
                          {newEmployeeData.isDoctor && !newEmployeeData.licenseNumber && 
                            <p className="text-xs text-red-500">Required for doctors</p>
                          }
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="caqhNumber">CAQH Number {newEmployeeData.isDoctor && <span className="text-red-500">*</span>}</Label>
                          <Input 
                            id="caqhNumber"
                            value={newEmployeeData.caqhNumber}
                            onChange={(e) => setNewEmployeeData({...newEmployeeData, caqhNumber: e.target.value})}
                            placeholder="Enter CAQH number"
                            required={newEmployeeData.isDoctor}
                          />
                          {newEmployeeData.isDoctor && !newEmployeeData.caqhNumber && 
                            <p className="text-xs text-red-500">Required for doctors</p>
                          }
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="npiNumber">NPI Number {newEmployeeData.isDoctor && <span className="text-red-500">*</span>}</Label>
                          <Input 
                            id="npiNumber"
                            value={newEmployeeData.npiNumber}
                            onChange={(e) => setNewEmployeeData({...newEmployeeData, npiNumber: e.target.value})}
                            placeholder="Enter NPI number"
                            required={newEmployeeData.isDoctor}
                          />
                          {newEmployeeData.isDoctor && !newEmployeeData.npiNumber && 
                            <p className="text-xs text-red-500">Required for doctors</p>
                          }
                        </div>
                      </div>
                    </div>
                    
                    {/* General professional information */}
                    <div className="grid gap-2">
                      <Label htmlFor="previousWorkExperience">Previous Work Experience</Label>
                      <Textarea 
                        id="previousWorkExperience"
                        value={newEmployeeData.previousWorkExperience}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, previousWorkExperience: e.target.value})}
                        placeholder="Enter previous work experience"
                        rows={3}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="professionalGoals">Professional Goals</Label>
                      <Textarea 
                        id="professionalGoals"
                        value={newEmployeeData.professionalGoals}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, professionalGoals: e.target.value})}
                        placeholder="Enter professional goals"
                        rows={3}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="notes">Additional Notes</Label>
                      <Textarea 
                        id="notes"
                        value={newEmployeeData.notes}
                        onChange={(e) => setNewEmployeeData({...newEmployeeData, notes: e.target.value})}
                        placeholder="Additional information or special instructions"
                        rows={3}
                      />
                    </div>
                  </TabsContent>
                  
                  {/* Access Controls Tab */}
                  <TabsContent value="access" className="space-y-4 mt-4">
                    {/* Scorecards visibility section */}
                    <div className="mb-4 pb-4 border-b dark:border-gray-700">
                      <h3 className="text-sm font-medium mb-3">Scorecards Access</h3>
                      
                      <div className="flex items-center space-x-2 py-2">
                        <Checkbox 
                          id="canViewAllScorecards" 
                          checked={newEmployeeData.canViewAllScorecards}
                          onCheckedChange={(checked) => 
                            setNewEmployeeData({
                              ...newEmployeeData, 
                              canViewAllScorecards: !!checked
                            })
                          }
                        />
                        <Label 
                          htmlFor="canViewAllScorecards" 
                          className="text-sm font-medium cursor-pointer"
                        >
                          Can view all scorecards across locations
                        </Label>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 ml-6 mb-2">
                        By default, employees can only view scorecards for their assigned locations. This permission allows viewing all scorecards.
                      </p>
                      
                      {/* Employee-specific scorecard access */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium mb-2">Individual Scorecard Access</h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                          Select which specific team members this employee can view scorecards for:
                        </p>
                        
                        <div className="grid gap-2 max-h-40 overflow-y-auto pr-2 border dark:border-gray-700 rounded-md p-2">
                          {Array.isArray(displayMembers) && displayMembers.map((member) => (
                            <div key={member.id} className="flex items-center space-x-2 py-1">
                              <Checkbox 
                                id={`scorecard-access-${member.id}`}
                                checked={newEmployeeData.specificScorecardAccess?.includes(member.id)}
                                onCheckedChange={(checked) => {
                                  const currentAccess = newEmployeeData.specificScorecardAccess || [];
                                  let newAccess;
                                  
                                  if (checked) {
                                    newAccess = [...currentAccess, member.id];
                                  } else {
                                    newAccess = currentAccess.filter(id => id !== member.id);
                                  }
                                  
                                  setNewEmployeeData({
                                    ...newEmployeeData,
                                    specificScorecardAccess: newAccess
                                  });
                                }}
                              />
                              <Label 
                                htmlFor={`scorecard-access-${member.id}`}
                                className="text-sm font-normal cursor-pointer flex items-center"
                              >
                                {member.fullName}
                                <Badge 
                                  variant="outline" 
                                  className="ml-2 text-xs font-normal"
                                >
                                  {getRoleLabel(member.role)}
                                </Badge>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Headlines access section */}
                    <div>
                      <h3 className="text-sm font-medium mb-3">Headlines Access</h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                        Control which types of headlines this employee can view. By default, employees only see headlines targeted to their role.
                      </p>
                      
                      <div className="grid gap-1">
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="ceoHeadlines" 
                            checked={newEmployeeData.headlineAccess.ceoHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  ceoHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="ceoHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            CEO Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="executiveHeadlines" 
                            checked={newEmployeeData.headlineAccess.executiveHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  executiveHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="executiveHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Executive Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="virtualExecutiveHeadlines" 
                            checked={newEmployeeData.headlineAccess.virtualExecutiveHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  virtualExecutiveHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="virtualExecutiveHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Virtual Executive Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="teamLeadHeadlines" 
                            checked={newEmployeeData.headlineAccess.teamLeadHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  teamLeadHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="teamLeadHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Team Lead Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="billingAndAuditingHeadlines" 
                            checked={newEmployeeData.headlineAccess.billingAndAuditingHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  billingAndAuditingHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="billingAndAuditingHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Billing &amp; Auditing Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="trainingHeadlines" 
                            checked={newEmployeeData.headlineAccess.trainingHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  trainingHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="trainingHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Training Headlines
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2 py-2">
                          <Checkbox 
                            id="trainerHeadlines" 
                            checked={newEmployeeData.headlineAccess.trainerHeadlines}
                            onCheckedChange={(checked) => 
                              setNewEmployeeData({
                                ...newEmployeeData, 
                                headlineAccess: {
                                  ...newEmployeeData.headlineAccess,
                                  trainerHeadlines: !!checked
                                }
                              })
                            }
                          />
                          <Label 
                            htmlFor="trainerHeadlines" 
                            className="text-sm cursor-pointer"
                          >
                            Training the Trainers
                          </Label>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
              <DialogFooter>
                <Button 
                  onClick={handleAddEmployee} 
                  disabled={
                    isAddingEmployee ||
                    !newEmployeeData.fullName.trim() ||
                    !newEmployeeData.email.trim() ||
                    !newEmployeeData.locationId ||
                    (user.isSuperAdmin && !newEmployeeData.organizationId)
                  }>
                  {isAddingEmployee ? 'Adding...' : 'Add Team Member'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Search and Role Tabs */}
      <div className="flex flex-col sm:flex-row justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="relative max-w-md w-full">
          <Input
            type="text"
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10"
          />
          <span className="mdi mdi-magnify absolute right-3 top-2.5 text-neutral-400"></span>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
          <TabsList className="overflow-x-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            {uniqueRoles.map(role => (
              <TabsTrigger key={role} value={role}>
                {getRoleLabel(role)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          // Improved detailed loading skeletons
          [...Array(6)].map((_, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="p-4 pb-2">
                <div className="flex flex-wrap gap-2 items-start">
                  <div className="flex min-w-0 flex-1 items-center">
                    <Skeleton className="h-10 w-10 rounded-full mr-3" />
                    <div>
                      <Skeleton className="h-5 w-32 mb-1" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                <div className="grid gap-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <div className="mt-3 flex items-center">
                    <Skeleton className="h-5 w-5 rounded-full mr-2" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between p-4 pt-0">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </CardFooter>
            </Card>
          ))
        ) : filteredMembers.length === 0 ? (
          <div className="col-span-3 p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 opacity-50">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold">No team members found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search criteria' : activeTab !== 'all' ? 'Try selecting a different role filter' : 'Add team members to get started'}
            </p>
            <Button onClick={() => {
              setSearchQuery('');
              setActiveTab('all');
              setSelectedLocation('');
            }} variant="outline" className="mt-4">
              Reset Filters
            </Button>
          </div>
        ) : (
          filteredMembers.map((member) => (
            <Card key={member.id} className="overflow-hidden">
              <CardHeader className="p-4 pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <Avatar className="h-10 w-10 mr-3">
                      <AvatarFallback className="bg-primary text-white">
                        {getInitials(member.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 break-words">
                      <CardTitle className="text-lg">{member.fullName}</CardTitle>
                      <p className="text-xs text-neutral-500 break-all">{member.email?.trim() || 'No login email'}</p>
                    </div>
                  </div>
                  <Badge className="max-w-full whitespace-normal">{getRoleLabel(member.role)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">Performance Score</span>
                    <span className="text-sm font-medium">{typeof member.score === 'number' && Number.isFinite(member.score) ? `${member.score}%` : '—'}</span>
                  </div>
                  <Progress 
                    value={typeof member.score === 'number' && Number.isFinite(member.score) ? member.score : 0}
                    className={`h-2 ${getStatusColor(member.status)}`} 
                  />
                </div>
                
                <div className="mt-4 text-sm">
                  <div className="flex items-center text-neutral-600 dark:text-neutral-300">
                    <span className="mdi mdi-map-marker-outline mr-1"></span>
                    Location: {member.location || 'No location'}
                  </div>
                </div>
              </CardContent>
              <CardFooter className="p-4 pt-0 flex justify-between items-center">
                <Button variant="outline" size="sm">
                  <span className="mdi mdi-clipboard-account-outline mr-1"></span>
                  Coach
                </Button>
                
                <Button 
                  asChild
                  variant="outline" 
                  size="sm"
                >
                  <a href={`/profile/${encodeURIComponent(member.id)}${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`}>
                  <span className="mdi mdi-eye-outline mr-1"></span>
                  View Profile
                  </a>
                </Button>
              </CardFooter>
            </Card>
          ))
        )}
      </div>

      {filteredMembers.length === 0 && !isLoading && (
        <Card className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">No Team Members Found</h3>
          <p className="text-neutral-500 dark:text-neutral-400 mb-4">
            {searchQuery ? 
              `No team members match your search for "${searchQuery}"` : 
              "No team members have been added to this location yet."}
          </p>
        </Card>
      )}
    </div>
  );
}
